import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Version, VersionFilters } from "../../types/version";
import EntityTable from "../EntityTable";
import { renderRowViewAndUpdateActions } from "../../common/TableUtils";
import {
  extractVersionEditLink,
  extractVersionViewLink,
} from "../entitiesReferences/InternalEntityLink";
import { MRT_ColumnDef } from "material-react-table";
import useVersionQuery from "../../hooks/query/useVersionQuery";
import useVersionLogic from "../../hooks/logic/versionLogic";
import useServerFilters from "../../hooks/useServerFilters";
import { versionFilterSpec } from "../../common/entities/VersionColumns";

interface VersionTableProps {
  columns?: MRT_ColumnDef<Version, any>[];
  hiddenColumnIds?: Extract<keyof Version, string>[];
  filters: VersionFilters;
  enableRowActions?: boolean;
}

const VersionTable: React.FC<VersionTableProps> = ({
  columns,
  hiddenColumnIds,
  filters,
  enableRowActions = true,
}) => {
  const {
    serverFilters,
    entityTableFilterProps,
    pagination,
    setPagination,
  } = useServerFilters(filters, versionFilterSpec);
  const { useFetchAll } = useVersionQuery(filters.system_language_type);
  const { languageBasedColumns } = useVersionLogic(filters.system_language_type);
  const { data, isLoading, errorMessage } = useFetchAll({
    filters: serverFilters,
    externalPagination: pagination,
    externalSetPagination: setPagination,
  });

  const navigate = useNavigate();

  const renderRowActions = useMemo(
    () =>
      renderRowViewAndUpdateActions<Version>(
        (row) => navigate(extractVersionViewLink(row)),
        (row) => navigate(extractVersionEditLink(row))
      ),
    [navigate]
  );

  return (
    <EntityTable
      loading={isLoading}
      error={errorMessage}
      columns={columns || languageBasedColumns}
      hiddenColumnIds={hiddenColumnIds}
      renderRowActions={renderRowActions}
      enableRowActions={enableRowActions}
      data={data?.data || []}
      pagination={pagination}
      manualPagination={true}
      onPaginationChange={setPagination}
      totalRowCount={data?.total || 0}
      manualFiltering
      {...entityTableFilterProps}
    />
  );
};

export default VersionTable;
